import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ServerEvent } from '@converoom/contracts';
import { api } from '../lib/api';
import { Realtime, type ConnState } from '../lib/realtime';
import { navigate, type Route } from '../lib/router';
import type { Me, Project, Room, Connection } from '../lib/types';
import { Mark } from '../components/Mark';
import { RoomView } from './RoomView';
import { Drawer } from './Drawer';
import { NudgeAlert } from '../components/Nudge';
import { Settings } from './Settings';
import { ProjectSettings } from './ProjectSettings';

export function Workspace({ me, route, onLogout }: { me: Me; route: Route; onLogout: () => void }) {
  const rt = useMemo(() => new Realtime(), []);
  const [conn, setConn] = useState<ConnState>('connecting');
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [railOpen, setRailOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(() => window.matchMedia('(min-width: 1181px)').matches);
  const [newProject, setNewProject] = useState('');

  const projectId = 'projectId' in route ? route.projectId : projects?.[0]?.id;
  const project = projects?.find((p) => p.id === projectId) ?? null;
  const roomId = route.name === 'room' ? route.roomId : null;
  const room = rooms.find((r) => r.id === roomId) ?? null;

  useEffect(() => {
    rt.start();
    const off1 = rt.onState(setConn);
    const off2 = rt.on((e: ServerEvent) => {
      if (e.t === 'error' && e.code === 'session_ended') onLogout();
      if (e.t === 'access.revoked' && !e.roomId) void loadProjects();
      if (e.t === 'access.revoked' && e.roomId && projectId) void loadRooms(projectId);
    });
    return () => {
      off1();
      off2();
      rt.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rt]);

  // Escape closes overlay menus (keyboard users, WCAG 2.1.2).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setRailOpen(false);
      if (!window.matchMedia('(min-width: 1181px)').matches) setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const loadProjects = useCallback(async () => setProjects(await api.get<Project[]>('/v1/projects')), []);
  const loadRooms = useCallback(
    async (pid: string) => setRooms(await api.get<Room[]>(`/v1/projects/${pid}/rooms`)),
    [],
  );
  const loadConnections = useCallback(
    async () => setConnections(await api.get<Connection[]>('/v1/me/connections')),
    [],
  );

  useEffect(() => {
    void loadProjects();
    void loadConnections();
  }, [loadProjects, loadConnections]);

  // Project presence: join only the project on screen (presence is project-scoped, CR-T74).
  useEffect(() => {
    if (!projectId) return;
    void loadRooms(projectId).catch(() => setRooms([]));
    rt.joinProject(projectId);
    return () => rt.leaveProject(projectId);
  }, [projectId, rt, loadRooms]);

  // Default navigation: first room of the current project.
  useEffect(() => {
    if (route.name === 'home' && projects?.length) navigate(`/p/${projects[0]!.id}`);
    if ((route.name === 'project' || route.name === 'home') && project && rooms.length)
      navigate(`/p/${project.id}/r/${rooms[0]!.id}`);
  }, [route, projects, project, rooms]);

  async function createProject() {
    if (!newProject.trim()) return;
    const p = await api.post<Project>('/v1/projects', { name: newProject.trim() });
    setNewProject('');
    await loadProjects();
    navigate(`/p/${p.id}`);
  }

  async function createRoom(kind: 'shared' | 'private') {
    if (!project) return;
    const name = prompt(kind === 'private' ? 'Özel oda adı' : 'Oda adı');
    if (!name?.trim()) return;
    const r = await api.post<Room>(`/v1/projects/${project.id}/rooms`, { name: name.trim(), kind });
    await loadRooms(project.id);
    navigate(`/p/${project.id}/r/${r.id}`);
  }

  async function logout() {
    await api.post('/v1/auth/logout').catch(() => {});
    rt.stop();
    onLogout();
  }

  const go = (path: string) => {
    setRailOpen(false);
    navigate(path);
  };

  const shellClass = [
    'shell',
    drawerOpen ? 'drawer-open' : 'drawer-closed',
    railOpen ? 'rail-open' : '',
  ].join(' ');
  const showDrawer = route.name === 'room' && room && project;

  return (
    <div className={shellClass}>
      <NudgeAlert rt={rt} />
      <a className="skip" href="#main">
        İçeriğe geç
      </a>
      <nav className="rail" aria-label="Projeler ve odalar">
        <div className="rail-head">
          <span className="brand">
            <Mark /> Converoom
          </span>
        </div>
        <div className="rail-section">
          <label className="field">
            <span>Proje</span>
            <select
              className="select"
              value={projectId ?? ''}
              onChange={(e) => go(`/p/${e.target.value}`)}
              aria-label="Proje seç"
            >
              {projects?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          {projects?.length === 0 && (
            <p className="hint">Henüz projeniz yok. Aşağıdan oluşturun veya bir davet bekleyin.</p>
          )}

          {project && (
            <>
              <p className="rail-title" id="rooms-title">
                Odalar
              </p>
              <ul className="list" aria-labelledby="rooms-title" style={{ gap: 0 }}>
                {rooms.map((r) => (
                  <li key={r.id} style={{ border: 0, padding: 0 }}>
                    <button
                      className="room-link"
                      aria-current={r.id === roomId ? 'page' : undefined}
                      onClick={() => go(`/p/${project.id}/r/${r.id}`)}
                    >
                      {r.name}
                      <span className="room-kind">
                        {r.kind === 'private' ? 'özel' : !r.aiEnabled ? 'AI kapalı' : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {project.role !== 'guest' && (
                <div style={{ display: 'flex', gap: 'var(--s1)', marginTop: 'var(--s2)', flexWrap: 'wrap' }}>
                  <button className="btn small ghost" onClick={() => createRoom('shared')}>
                    Oda ekle
                  </button>
                  <button className="btn small ghost" onClick={() => createRoom('private')}>
                    Özel oda ekle
                  </button>
                </div>
              )}
              <button
                className="btn small ghost"
                style={{ marginTop: 'var(--s1)' }}
                onClick={() => go(`/p/${project.id}/settings`)}
              >
                Proje ayarları
              </button>
            </>
          )}

          <p className="rail-title">Yeni proje</p>
          <form
            style={{ display: 'flex', gap: 'var(--s1)' }}
            onSubmit={(e) => {
              e.preventDefault();
              void createProject();
            }}
          >
            <input
              className="input"
              placeholder="Proje adı"
              value={newProject}
              onChange={(e) => setNewProject(e.target.value)}
              aria-label="Yeni proje adı"
            />
            <button className="btn" type="submit">
              Oluştur
            </button>
          </form>
        </div>
        <div className="rail-foot">
          <span style={{ fontSize: 14, fontWeight: 500 }}>{me.displayName}</span>
          <span className="spacer" style={{ flex: 1 }} />
          <button className="btn small ghost" onClick={() => go('/settings/connections')}>
            Ayarlar
          </button>
          <button className="btn small ghost" onClick={logout}>
            Çıkış
          </button>
        </div>
      </nav>
      <button className="scrim for-rail" aria-label="Menüyü kapat" onClick={() => setRailOpen(false)} />

      <main className="main" id="main">
        {route.name === 'settings' ? (
          <Settings
            tab={route.tab}
            me={me}
            projects={projects ?? []}
            onConnectionsChanged={loadConnections}
            onOpenMenu={() => setRailOpen(true)}
          />
        ) : route.name === 'projectSettings' && project ? (
          <ProjectSettings
            project={project}
            me={me}
            onOpenMenu={() => setRailOpen(true)}
            onChanged={() => loadRooms(project.id)}
          />
        ) : room && project ? (
          <RoomView
            key={room.id}
            me={me}
            project={project}
            room={room}
            rt={rt}
            conn={conn}
            connections={connections}
            onOpenMenu={() => setRailOpen(true)}
            onToggleDrawer={() => setDrawerOpen((v) => !v)}
            drawerOpen={drawerOpen}
          />
        ) : (
          <div className="page">
            <div className="page-inner">
              <button className="btn only-narrow" onClick={() => setRailOpen(true)}>
                Menü
              </button>
              <div className="empty">
                <h2>
                  {projects === null
                    ? 'Yükleniyor…'
                    : project
                      ? 'Bir oda seçin'
                      : 'Bir proje seçin veya oluşturun'}
                </h2>
              </div>
            </div>
          </div>
        )}
      </main>

      {showDrawer && (
        <>
          <aside className="drawer" id="project-drawer" aria-label="Proje paneli">
            <Drawer
              me={me}
              project={project}
              room={room}
              rt={rt}
              conn={conn}
              onClose={() => setDrawerOpen(false)}
            />
          </aside>
          <button
            className="scrim for-drawer only-drawer-overlay"
            aria-label="Paneli kapat"
            onClick={() => setDrawerOpen(false)}
          />
        </>
      )}
    </div>
  );
}
